import type { ErrorEvent, EventHint } from '@sentry/react';
import { isAxiosError } from 'axios';

// A failed mutation is already shown to the user as a toast, yet it also surfaces as an unhandled
// rejection, and with a stack made only of axios frames every API failure used to collapse into a
// single Sentry issue. A 4xx carrying only a `message` is a business rule the user just read: not a
// bug. A 4xx with `errors` is a Zod rejection — the web sent a payload the API refuses — and 5xx or
// network failures are real faults: keep those, one issue per endpoint.
export function filterApiErrorEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException;
  if (!isAxiosError(error)) return event;

  const status = error.response?.status;
  const data = error.response?.data as { message?: unknown; errors?: unknown } | undefined;
  const isValidationError = Array.isArray(data?.errors);
  if (status !== undefined && status >= 400 && status < 500 && !isValidationError) return null;

  const method = (error.config?.method ?? 'get').toUpperCase();
  const endpoint = toEndpointTemplate(error.config?.url ?? '');
  const statusLabel = status === undefined ? 'network' : String(status);

  event.fingerprint = ['api-error', method, endpoint, statusLabel];
  event.tags = {
    ...event.tags,
    'api.endpoint': `${method} ${endpoint}`,
    'api.status': statusLabel,
  };
  event.extra = { ...event.extra, apiResponse: data };
  return event;
}

function toEndpointTemplate(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  return path.replace(/\/\d+(?=\/|$)/g, '/:id');
}
